import React, { useEffect, useState } from 'react';
import {
  CheckCircle2,
  LogOut,
  LayoutDashboard,
  Eye,
  EyeOff,
  ArrowLeft
} from 'lucide-react';

import Producao from './Producao';
import Faturamento from './Faturamento';
import Expedicao from './Expedicao';
import Conta from './Conta';
import Estoque from './Estoque';
import Login from './Login';
import ContaPagar from './ContaPagar';
import ContaPagarDetalhe from './ContaPagarDetalhe';
import ContaReceberDetalhe from './ContaReceberDetalhe';
import GraficoDashboardCompleto from './GraficoDashboardCompleto.jsx';
import Sidebar from './components/dashboard/Sidebar';
import SectionPlaceholder from './components/dashboard/SectionPlaceholder';
import FinancialDashboard from './components/dashboard/FinancialDashboard';
import ShippingDashboard from './components/dashboard/ShippingDashboard';
import ProductionDashboard from './components/dashboard/ProductionDashboard';
import { CompanyFilter } from './components/dashboard/CompanyFilter';
import { useDashboardSummary } from './lib/useDashboardSummary';
import { clearSession, readSessionPermissions } from './lib/session-permissions';

const sectionNames = {
  comercial: 'Comercial',
  financeiro: 'Financeiro',
  faturamento: 'Faturamento',
  'financeiro-pagamentos': 'Lista Pagamento',
  'financeiro-recebimentos': 'Lista Recebimento',
  producao: 'Produção',
  'producao-painel': 'Painel de Produção',
  expedicao: 'Expedição',
  'expedicao-painel': 'Painel de Expedição',
  logistica: 'Logística',
  'logistica-pde': 'PDE - Programação de Entrega',
};

export default function App() {
  const [valorRealizado, setValorRealizado] = useState(0);
  const [empresaSelecionada, setEmpresaSelecionada] = useState('');
  const [loading, setLoading] = useState(true);
  const [isLogged, setIsLogged] = useState(false);
  const base_url = process.env.REACT_APP_API_URL || 'http://localhost:5008';
  const [valorDevolucoes, setValorDevolucoes] = useState(0);
  const [mostraProducao, setMostraProducao] = useState(0);
  const [mostraRecebiveis, setMostraRecebiveis] = useState(0);
  const [mostraFaturamento, setMostraFaturamento] = useState(0);
  const [mostraExpedicao, setMostraExpedicao] = useState(0);
  const [mostraContaPagar, setMostraContaPagar] = useState(0);
  const [utilizaBordo, setUtilizaBordo] = useState(0);
  const [usuario, setUsuario] = useState("");
  const [showEstoque, setShowEstoque] = useState(true);
  const [showDashboardPage, setShowDashboardPage] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  
  // Sinal para forçar a atualização dos componentes filhos
  const [refreshSignal, setRefreshSignal] = useState(0);
  const { data, loading: generalLoading, error: generalError } = useDashboardSummary('/api/dashboard/geral', { empresa: empresaSelecionada, refreshSignal, enabled: isLogged && utilizaBordo === 1 });

  useEffect(() => {
    document.title = "Onboard";
  }, []);

  function loadSession() {
    const token = localStorage.getItem('@user_token');
    const permissions = readSessionPermissions();
    const nomeUsuario = localStorage.getItem('@NomeUsuario');
     
    setUtilizaBordo(permissions.utilizaBordo);
    setMostraRecebiveis(permissions.mostraRecebiveis);
    setMostraContaPagar(permissions.mostraContaPagar);
    setMostraFaturamento(permissions.mostraFaturamento);
    setMostraExpedicao(permissions.mostraExpedicao);
    setMostraProducao(permissions.mostraProducao);
    setUsuario(nomeUsuario || "");

    if (token) {
      setIsLogged(true);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }

  useEffect(() => { loadSession(); }, []);

  useEffect(() => {
    let interval;
    if (isLogged && utilizaBordo === 1) {
      const cincoMinutos = 5 * 60 * 1000;

      interval = setInterval(() => {
        const token = localStorage.getItem('@user_token');        
        if (token) {
          console.log("Atualizando painel automaticamente...");
          // Os indicadores consultam novamente ao mudar refreshSignal.          
          setRefreshSignal(prev => prev + 1);
        }
      }, cincoMinutos);
    }

    return () => clearInterval(interval);
  }, [isLogged, utilizaBordo]);

  const handleLogout = () => {
    clearSession();
    setIsLogged(false);
    window.location.reload();
  };

  if (loading) return (
    <div className="h-screen bg-[#0d1117] flex flex-col items-center justify-center text-green-500 font-mono italic animate-pulse">
      <LayoutDashboard size={48} className="mb-4" />
      <span className="tracking-[0.3em]">AUTENTICANDO SISTEMA...</span>
    </div>
  );

  if (!isLogged) {
    return <Login onLoginSuccess={loadSession} />;
  }

  if (utilizaBordo !== 1) {
    return <div className="min-h-screen bg-[#0d1117] flex flex-col items-center justify-center gap-4 text-white">
      <p role="alert">Usuário sem permissão de Acesso</p>
      <button type="button" onClick={handleLogout}>Sair do Sistema</button>
    </div>;
  }

  if (showDashboardPage && mostraFaturamento === 1) {
    return <GraficoDashboardCompleto empresaInicial={empresaSelecionada || "TODAS"} onClose={() => setShowDashboardPage(false)} />;
  }

  const metaMes = data?.metaMes || 0;
  const percentualMeta = metaMes > 0 ? ((valorRealizado / metaMes) * 100).toFixed(2) : 0;

  return (
    <div className="min-h-screen bg-[#0d1117] text-gray-400 font-sans lg:p-4">
      <div className="mx-auto flex max-w-[1600px] flex-col overflow-hidden border-gray-800 bg-[#0d1117] lg:min-h-[calc(100vh-2rem)] lg:flex-row lg:border">
        <Sidebar
          activeSection={activeSection}
          onNavigate={section => {
            if (section === 'faturamento-painel') {
              if (mostraFaturamento === 1) setShowDashboardPage(true);
              return;
            }
            setActiveSection(section);
          }}
          disabledSections={[...(mostraRecebiveis === 1 || mostraContaPagar === 1 ? [] : ['financeiro']), ...(mostraRecebiveis === 1 ? [] : ['financeiro-recebimentos']), ...(mostraContaPagar === 1 ? [] : ['financeiro-pagamentos']), ...(mostraFaturamento === 1 ? [] : ['faturamento']), ...(mostraExpedicao === 1 ? [] : ['expedicao']), ...(mostraProducao === 1 ? [] : ['producao'])]}
        />

        <main className={`min-w-0 flex-1 p-4 ${activeSection === 'home' ? 'lg:px-6 lg:py-4' : 'lg:p-8'}`}>
          <div className={activeSection === 'home' ? 'space-y-3' : 'space-y-4'}>

        <header className="flex justify-between items-center border-b border-gray-800 pb-2">
          <div>
            <h1 className="text-white tracking-[0.8em] uppercase text-[10px] font-black opacity-50">
              COMPUTADOR DE BORDO
            </h1>
            <h1 className="text-white tracking-[0.8em] uppercase text-[5px] font-black opacity-45">
              Login: {usuario}
            </h1>
            {activeSection !== 'home' && (
              <p className="mt-3 text-xs font-black uppercase tracking-[0.22em] text-white">
                {sectionNames[activeSection]}
              </p>
            )}
          </div>
          {['producao', 'producao-painel', 'expedicao', 'expedicao-painel'].includes(activeSection) ? (
            <button
              type="button"
              onClick={() => setActiveSection('home')}
              className="inline-flex items-center gap-2 self-start rounded-md border border-slate-700 px-3 py-2 text-xs font-bold text-slate-300 transition-colors hover:border-blue-500 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
            >
              <ArrowLeft size={15} /> Voltar ao resumo
            </button>
          ) : <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[9px] text-red-500 hover:text-red-400 font-bold uppercase transition-colors"
          >
            <LogOut size={14} /> Sair do Sistema
          </button>}
        </header>

        {generalError && <p role="alert" className="text-xs text-red-300">{generalError}</p>}
        {activeSection === 'home' ? (
          <>
        {(mostraFaturamento === 1 || mostraExpedicao === 1) && <div className="bg-[#161b22] px-4 py-2 rounded border border-gray-800 shadow-2xl">
          {mostraFaturamento === 1 && <>
          <div className="flex justify-between items-center mb-2">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-500" />
                <span className="text-[10px] font-bold uppercase">{empresaSelecionada ? "Meta consolidada do Mês:" : "Meta do Mês:"}
                  <span className="text-green-500 ml-2 font-mono italic">
                    {generalLoading || generalError ? '—' : `R$ ${Number(metaMes).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </span>
                </span>
              </div>
            </div>
            <span className="text-green-500 font-bold font-mono">{empresaSelecionada || generalLoading || generalError ? '—' : `${percentualMeta}%`}</span>
          </div>

          {!empresaSelecionada && !generalLoading && !generalError && <div className="my-2">
            <div className="w-full bg-gray-900 h-2 rounded-full overflow-hidden border border-gray-800">
              <div
                className="bg-gradient-to-r from-orange-600 to-green-500 h-full transition-all duration-1000"
                style={{ width: `${Math.min(percentualMeta, 100)}%` }}
              />
            </div>
          </div>}

          </>}
          <div className="flex items-center w-full">
            <button
              onClick={() => setShowEstoque(!showEstoque)}
              className="ml-auto flex items-center gap-2 px-2 py-1 bg-gray-900 border border-gray-700 rounded hover:bg-gray-800 transition-all"
            >
              {showEstoque ? (
                <EyeOff size={14} className="text-red-500" />
              ) : (
                <Eye size={14} className="text-blue-500" />
              )}
              <span className="text-[9px] font-black uppercase tracking-tighter">
                {showEstoque ? "Ocultar Estoque" : "Mostrar Estoque"}
              </span>
            </button>
          </div>
        </div>}
        <CompanyFilter value={empresaSelecionada} onChange={setEmpresaSelecionada} /> 

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">        
                 
          {mostraFaturamento === 1 && (            
            <Faturamento
              empresa={empresaSelecionada}
              onDataLoad={setValorDevolucoes}
              onRealizadoLoad={setValorRealizado}             
              valorMeta={empresaSelecionada ? 0 : metaMes}
               refreshSignal={refreshSignal} 
              onOpenDashboard={() => setShowDashboardPage(true)}
            />
          )}
          {mostraExpedicao === 1 && (
            <Expedicao empresa={empresaSelecionada} refreshSignal={refreshSignal}
              onOpenDashboard={() => setActiveSection('expedicao-painel')} />
          )}
          {mostraProducao === 1 && (
            <Producao empresa={empresaSelecionada} refreshSignal={refreshSignal}
              onOpenDashboard={() => setActiveSection('producao-painel')} />
          )}
        </div>
         
        {showEstoque && (mostraFaturamento === 1 || mostraExpedicao === 1) && (
          <Estoque
            empresa={empresaSelecionada}
            refreshSignal={refreshSignal}
            pedidosCancelados={data?.pedidosCancelados || 0}
            valordevolucoesMes={valorDevolucoes || 0}
            mostraFaturamento={mostraFaturamento || 0}
            mostraExpedicao={mostraExpedicao || 0}
          />
        )}

        {(mostraRecebiveis === 1 || mostraContaPagar === 1) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mostraRecebiveis === 1 && <Conta empresa={empresaSelecionada} refreshSignal={refreshSignal} />}
            {mostraContaPagar === 1 && <ContaPagar empresa={empresaSelecionada} refreshSignal={refreshSignal} />}
          </div>
        )}
          </>
        ) : (activeSection === 'expedicao' || activeSection === 'expedicao-painel') && mostraExpedicao === 1 ? (
          <ShippingDashboard apiBaseUrl={base_url} initialCompany={empresaSelecionada} refreshSignal={refreshSignal} />
        ) : (activeSection === 'producao' || activeSection === 'producao-painel') && mostraProducao === 1 ? (
          <ProductionDashboard apiBaseUrl={base_url} initialCompany={empresaSelecionada} refreshSignal={refreshSignal} />
        ) : activeSection === 'financeiro' && (mostraRecebiveis === 1 || mostraContaPagar === 1) ? (
          <FinancialDashboard apiBaseUrl={base_url} onSessionExpired={handleLogout} canViewReceipts={mostraRecebiveis === 1} canViewPayments={mostraContaPagar === 1} />
        ) : activeSection === 'financeiro-pagamentos' && mostraContaPagar === 1 ? (
          <ContaPagarDetalhe onClose={() => setActiveSection('financeiro')} />
        ) : activeSection === 'financeiro-recebimentos' && mostraRecebiveis === 1 ? (
          <ContaReceberDetalhe onClose={() => setActiveSection('financeiro')} />
        ) : (
          <SectionPlaceholder sectionName={sectionNames[activeSection]} />
        )}

        <footer className={`text-center opacity-50 ${activeSection === 'home' ? 'pt-2' : 'pt-8'}`}>
          <p className="text-[8px] uppercase tracking-[0.5em]">Sistema de Gestão Realtime</p>
        </footer>
          </div>
        </main>
      </div>
    </div>
    
  );

}
